import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {assertAndContinue} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrameRenderOverrides} from 'data-frames/table/DataFrameTable';
import {headerCellStyles} from 'data-frames/table/header/DataFrameTableHeaderCell';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {readableTypeDef} from 'data-types/benchling-type-def/readableTypeDef';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {BLUE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {capitalize} from 'util/NameUtil';
import {JSONValue} from 'util/Types';

const ITERATION_LIMIT = 100;

type FlagColumnStepColumnSystemNames =
  | {flagColumn: string; commentColumn: string}
  | {flagColumn: null; commentColumn: null};

export function getFlagRowsStepSystemNamesFromAncestorSteps(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): FlagColumnStepColumnSystemNames {
  // Get ancestor steps until we reach a merging step (a step with multiple input steps)
  const ancestorSteps = [step]; // downstream steps first, upstream steps later
  for (let i = 0; i <= ITERATION_LIMIT; i++) {
    const currentStep = ancestorSteps[ancestorSteps.length - 1];
    const parentSteps = getParentSteps(analysis, currentStep);
    if (parentSteps.length !== 1) {
      break;
    }
    ancestorSteps.push(parentSteps[0]);
    assertAndContinue(i < ITERATION_LIMIT, 'Iteration limit reached');
  }

  const nullFlagColumnStepColumnSystemNames = {
    flagColumn: null,
    commentColumn: null,
  } as const;
  // Find the most recent FLAG_ROWS ancestor that adds two columns
  let activeFlagRowStepIndex: number | null = null;
  let flagRowStepOutputColumns: Array<DataFrameColumnDef> | null = null;
  for (let i = 0; i < ancestorSteps.length; i++) {
    const currentStep = ancestorSteps[i];
    if (currentStep.definition.stepType === PipelineStepType.FLAG_ROWS) {
      if (
        currentStep.outputStepDatas[0].dataFrames.length === 0 ||
        currentStep.inputStepDatas[0].dataFrames.length === 0
      ) {
        return nullFlagColumnStepColumnSystemNames;
      }
      const outputColumns = currentStep.outputStepDatas[0].dataFrames[0].allColumnsJson;
      const inputColumns = currentStep.inputStepDatas[0].dataFrames[0].allColumnsJson;
      if (outputColumns.length === inputColumns.length + 2) {
        activeFlagRowStepIndex = i;
        flagRowStepOutputColumns = outputColumns;
        break;
      }
    }
  }

  if (activeFlagRowStepIndex == null || flagRowStepOutputColumns == null) {
    return nullFlagColumnStepColumnSystemNames;
  }

  const flagColumnName = flagRowStepOutputColumns[0].systemName;
  const commentColumnName = flagRowStepOutputColumns[flagRowStepOutputColumns.length - 1].systemName;
  const flagColumnStepColumnSystemNames = {
    flagColumn: flagColumnName,
    commentColumn: commentColumnName,
  };

  // Look at all steps between current view step and the active flag step, to see if the flag/comment columns
  // were removed or changed type
  const ancestorStepsBeforeLastFlagRowsStep = ancestorSteps.slice(0, activeFlagRowStepIndex).reverse();
  for (const ancestorStep of ancestorStepsBeforeLastFlagRowsStep) {
    if (ancestorStep.outputStepDatas[0].dataFrames.length === 0) {
      return nullFlagColumnStepColumnSystemNames;
    }
    const outputDataFrameColumns = ancestorStep.outputStepDatas[0].dataFrames[0].allColumnsJson;
    const flagColumn = outputDataFrameColumns.find((column) => column.systemName === flagColumnName);
    const commentColumn = outputDataFrameColumns.find((column) => column.systemName === commentColumnName);
    if (
      flagColumn == null ||
      commentColumn == null ||
      flagColumn.type.leafType !== LeafType.BOOLEAN ||
      commentColumn.type.leafType !== LeafType.TEXT
    ) {
      // The columns are not preserved in the output dataframe, drop them as flag/comment columns
      return nullFlagColumnStepColumnSystemNames;
    }
  }

  return flagColumnStepColumnSystemNames;
}

/**
 * This function finds the flag column that need to be rendered as flag in the data and returns a renderer for it.
 *
 * It traces up the lineage, stops at a merging step (e.g. join, union, a step that combines multiple dataframes)
 * and then find the series of flag_rows, and hide_columns steps and work out the special flag column at the current step.
 *
 * Note, the logic need to keep in sync with backend logic: benchling/pipelines/step_execution_library/execution_functions/execute_flag_rows.py
 * such that the flag column is identified correctly.
 *
 * @param lineageAncestorSteps - The ancestor steps of the current
 */
export default function getDataFrameRenderOverrides(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): DataFrameRenderOverrides {
  if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
    return {};
  }
  const flagColumnStepColumnSystemNames = getFlagRowsStepSystemNamesFromAncestorSteps(analysis, step);
  if (flagColumnStepColumnSystemNames.flagColumn == null) {
    return {};
  }
  const dataFrameRenderOverrides = {
    columnNameToDataCellRenderFn: {
      [flagColumnStepColumnSystemNames.flagColumn]: (cellValue: JSONValue) => {
        if (cellValue === true) {
          return <BenchlingIcon className={css(styles.icon)} icon="flag" />;
        } else {
          return <></>;
        }
      },
    },
    columnNameToHeaderCellRenderFn: {
      [flagColumnStepColumnSystemNames.flagColumn]: (column: DataFrameColumnDef) => (
        <>
          <Tooltip placement={TooltipPlacement.TOP} title={capitalize(readableTypeDef(column.type.leafType))}>
            <BenchlingIcon className={css(headerCellStyles.typeIcon)} icon="flag" />
          </Tooltip>
        </>
      ),
      [flagColumnStepColumnSystemNames.commentColumn]: (column: DataFrameColumnDef) => (
        <>
          <Tooltip placement={TooltipPlacement.TOP} title={capitalize(readableTypeDef(column.type.leafType))}>
            <BenchlingIcon className={css(headerCellStyles.typeIcon)} icon="comment" />
          </Tooltip>
          <span className="u-mini-spacing-left">{column.displayName}</span>
        </>
      ),
    },
  };

  return dataFrameRenderOverrides;
}

const styles = StyleSheet.create({
  icon: {
    color: BLUE,
  },
});
